import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import { axisTicks, overruns, sampleTrack, type Track } from './chart'
import { onFrame } from './ticker'

/**
 * Eine Spur über den Gangzyklus — randlos, flach, mehrere untereinander.
 *
 * WARUM NICHT `CycleChart`. Das grosse Diagramm trägt Achsenbeschriftung,
 * Achsnamen und eine Fusszeile; es kostet links 62 und unten 40 Pixel, bevor
 * die erste Kurve gezeichnet ist. Das ist richtig, wenn EIN Bild eine Frage
 * beantwortet. Sechs davon untereinander sind Randfläche mit Kurven dazwischen.
 *
 * Hier steht das Bild randlos über die volle Breite, 84 Pixel hoch, und die
 * Zahlen, die sonst an der Achse stünden, stehen in der Kopf- und Fusszeile
 * daneben: der abgelesene Wert je Kanal oben, der Wertebereich unten. Damit
 * passen sechs Spuren auf einen Schirm, und das ist der Punkt — man vergleicht
 * Spuren, indem man sie gleichzeitig sieht.
 *
 * GEMISCHTE EINHEITEN WERDEN NORMIERT, NICHT VERBOTEN. Wer wissen will, ob das
 * Kniemoment vor oder nach der Bodenkraft seine Spitze hat, muss beide
 * übereinanderlegen. Auf einer gemeinsamen Achse läge die kleinere Grösse flach
 * am Boden — nicht weil sie klein ist, sondern weil die Skala einer anderen
 * Einheit gehört. Also bekommt in einer gemischten Spur jeder Kanal seinen
 * eigenen Bereich, die Achse sagt „normiert je Kanal", und die Fusszeile nennt
 * die Bereiche einzeln. Die FORM ist dann vergleichbar, die Zahlenwerte sind es
 * nicht, und niemand kann das verwechseln.
 */

export interface TraceSeries {
  key: string
  label: string
  unit: string
  values: Track
  /** Welche der acht Serienfarben — eine Nummer, kein Farbwert. */
  tone: number
  /** Die mechanische Grenze dieser Kurve, wo es eine gibt. */
  limit?: { lo: number; hi: number }
}

export interface TraceChartProps {
  series: TraceSeries[]
  height: number
  /** Anteil des Zyklus, den die Standphase einnimmt — als senkrechte Marke. */
  stance?: number
  /**
   * Die Abspielmarke — dieselbe Referenz, aus der die Bühne ihre Haltung nimmt.
   *
   * Eine REFERENZ und kein Wert: sie ändert sich sechzigmal je Sekunde. Als
   * Eigenschaft gereicht, löste jede Spur einen Renderdurchlauf aus. Stattdessen
   * werden Marke, Punkte und die Zahlen in der Kopfzeile im Bildtakt direkt am
   * Knoten nachgezogen.
   */
  cursor?: MutableRefObject<number>
  /** Wohin die abgelesenen Werte geschrieben werden — je Kanal ein Knoten. */
  readouts?: MutableRefObject<Record<string, HTMLElement | null>>
  /** Wie viele Nachkommastellen je Kanal. */
  decimals?: Record<string, number>
  /** Anklicken springt an diese Stelle im Zyklus. */
  onScrub?: (at: number) => void
}

/** Oben und unten je fünf Pixel Luft — wie in der Vorlage. */
const PAD = 5

export interface TraceScale {
  mixed: boolean
  unit: string
  lo: number
  hi: number
  ticks: number[]
  /** Bei gemischten Einheiten: der eigene Bereich je Kanal. */
  per: Record<string, [number, number]>
}

/**
 * Der Wertebereich einer Spur.
 *
 * Er steht als eigene Funktion da, weil die Fusszeile ihn ebenso braucht wie das
 * Bild — und zwei Wege zu derselben Zahl wären zwei Wahrheiten.
 */
export function traceScale(series: TraceSeries[]): TraceScale {
  const units = new Set(series.map((s) => s.unit))
  const per: Record<string, [number, number]> = {}
  let lo = Infinity
  let hi = -Infinity

  for (const s of series) {
    let a = Infinity
    let b = -Infinity
    for (const v of s.values) {
      if (!Number.isFinite(v)) continue
      if (v < a) a = v
      if (v > b) b = v
    }
    if (!Number.isFinite(a)) {
      a = 0
      b = 1
    }
    if (b - a < 1e-9) b = a + 1
    per[s.key] = [a, b]
    if (a < lo) lo = a
    if (b > hi) hi = b
  }

  if (!Number.isFinite(lo)) {
    lo = 0
    hi = 1
  }
  /* Die Nullinie gehört ins Bild, auch wenn die Werte sie nicht schneiden: ohne
     sie sieht ein Moment, das nie das Vorzeichen wechselt, aus wie eines, das
     es tut. */
  lo = Math.min(lo, 0)
  hi = Math.max(hi, 0)
  if (hi - lo < 1e-9) hi = lo + 1

  /*
    AUF DIE NÄCHSTE TEILUNG AUFGERUNDET und nicht auf den Messwert gesetzt. Eine
    Achse, die genau am Höchstwert endet, lässt die Spitze am oberen Rand kleben
    — und bei 84 Pixeln Höhe sieht das aus wie eine abgeschnittene Kurve. Der
    Rand kostet nichts und beantwortet die Frage, ob da noch etwas käme.
  */
  const ticks = axisTicks(lo, hi)
  const step = ticks.length > 1 ? Math.abs(ticks[1] - ticks[0]) : hi - lo
  const round = step > 0 ? step : 1
  const outLo = Math.min(Math.floor(lo / round) * round, ticks[0] ?? lo)
  const outHi = Math.max(Math.ceil(hi / round) * round, ticks[ticks.length - 1] ?? hi)

  return {
    mixed: units.size > 1,
    unit: units.size === 1 ? series[0].unit : '',
    lo: outLo,
    hi: outHi,
    ticks,
    per,
  }
}

export function TraceChart({
  series,
  height,
  stance,
  cursor,
  readouts,
  decimals,
  onScrub,
}: TraceChartProps) {
  const id = useId()
  /*
    DIE BREITE WIRD GEMESSEN.

    Die Spur ist so hoch wie eingestellt und so breit wie die Spalte — ein
    Seitenverhältnis gibt es nicht. `preserveAspectRatio="none"` wäre der kurze
    Weg dahin und der falsche: er streckt die Zeichenfläche ungleichmässig, aus
    Punkten werden Ellipsen und die Strichstärke wird richtungsabhängig. Mit der
    gemessenen Breite ist eine Einheit im Bild ein Bildschirmpixel, und beides
    stimmt ohne Kunstgriff.
  */
  const node = useRef<SVGSVGElement>(null)
  const [measured, setMeasured] = useState(0)
  useLayoutEffect(() => {
    const el = node.current
    /* Nicht in jeder Umgebung vorhanden — in der Testumgebung etwa nicht. Ohne
       Beobachter bleibt es bei der Ersatzbreite. */
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => setMeasured(Math.round(entry.contentRect.width)))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const W = measured || 1000
  const H = height
  const top = PAD
  const bot = H - PAD

  const scale = useMemo(() => traceScale(series), [series])

  const y = (v: number) => bot - ((v - scale.lo) / (scale.hi - scale.lo)) * (bot - top)
  /* Normiert: jeder Kanal auf seinen eigenen Bereich, mit einem Streifen Luft
     unten, damit sich zwei Kurven am Boden nicht überdecken. */
  const yn = (v: number, key: string) => {
    const [a, b] = scale.per[key] ?? [0, 1]
    return bot - 0.06 * (bot - top) - ((v - a) / (b - a)) * (bot - top) * 0.88
  }
  const at = (v: number, key: string) => (scale.mixed ? yn(v, key) : y(v))

  const path = (s: TraceSeries): string => {
    const n = s.values.length
    let d = ''
    for (let i = 0; i < n; i++) {
      const v = s.values[i]
      if (!Number.isFinite(v)) continue
      d += `${d ? 'L' : 'M'}${((i / (n - 1)) * W).toFixed(1)} ${at(v, s.key).toFixed(2)}`
    }
    return d
  }

  /* Überschreitungen einmal je Kurve und nicht je Bild: sie hängen an den
     Werten und an der Grenze, und beide stehen still, solange niemand am Modell
     dreht. Bei normierter Darstellung entfallen sie — eine Grenze in einem Bild
     ohne gemeinsame Achse wäre eine Linie ohne Bedeutung. */
  const breaches = useMemo(() => {
    if (scale.mixed) return []
    const out: { key: string; from: number; to: number }[] = []
    for (const s of series) {
      if (!s.limit) continue
      for (const run of overruns(s.values, s.limit.lo, s.limit.hi)) out.push({ key: s.key, ...run })
    }
    return out
  }, [series, scale.mixed])

  const limitLines = useMemo(() => {
    if (scale.mixed) return []
    const values = new Set<number>()
    for (const s of series) {
      if (!s.limit) continue
      values.add(s.limit.lo)
      values.add(s.limit.hi)
    }
    return [...values].filter((v) => v > scale.lo && v < scale.hi)
  }, [series, scale])

  /* ------------------------------------------------------------------
     Die Abspielmarke, im Bildtakt und ohne Renderdurchlauf
     ------------------------------------------------------------------ */

  const headLine = useRef<SVGLineElement | null>(null)
  const dots = useRef<(SVGCircleElement | null)[]>([])

  useEffect(() => {
    if (!cursor) return
    let last = -1
    return onFrame(() => {
      const t = Math.min(1, Math.max(0, cursor.current))
      /* Nichts anfassen, solange sich die Stelle nicht bewegt hat — bei
         angehaltener Wiedergabe kostet die Seite dann nichts. */
      if (Math.abs(t - last) < 1e-4) return
      last = t
      const px = t * W
      const line = headLine.current
      if (line) {
        line.setAttribute('x1', String(px))
        line.setAttribute('x2', String(px))
      }
      series.forEach((s, i) => {
        const value = sampleTrack(s.values, t)
        const ok = Number.isFinite(value)
        const dot = dots.current[i]
        if (dot) {
          dot.setAttribute('cx', String(px))
          dot.setAttribute('cy', ok ? String(at(value, s.key)) : '-99')
          dot.setAttribute('opacity', ok ? '1' : '0')
        }
        const out = readouts?.current[s.key]
        if (out) {
          out.textContent = ok
            ? `${value >= 0 ? '' : '−'}${Math.abs(value).toFixed(decimals?.[s.key] ?? 1)} ${s.unit}`.trim()
            : '–'
          /* Eine überschrittene Grenze wird am Wert markiert und nicht nur an
             der Fläche im Bild: wer auf die Zahl schaut, soll es dort sehen. */
          const bad = ok && s.limit ? value < s.limit.lo || value > s.limit.hi : false
          out.classList.toggle('cchip__value--out', bad)
        }
      })
    })
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- at hängt an scale */
  }, [cursor, series, scale, readouts, decimals])

  return (
    <svg
      ref={node}
      className="trace__svg"
      viewBox={`0 0 ${W} ${H}`}
      style={{ height: `${H}px` }}
      role="img"
      aria-label={series.map((s) => `${s.label}, ${s.unit}`).join('; ')}
      onPointerDown={
        onScrub &&
        ((event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          onScrub(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)))
        })
      }
      onPointerMove={
        onScrub &&
        ((event) => {
          if (!event.buttons) return
          const rect = event.currentTarget.getBoundingClientRect()
          onScrub(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)))
        })
      }
    >
      {/* Überschreitungen liegen UNTER dem Gitter: sie sind Fläche, kein Text. */}
      {breaches.map((run, i) => (
        <rect
          key={`${id}-breach-${i}`}
          className="trace__breach"
          x={run.from * W}
          y={top}
          width={Math.max(2, (run.to - run.from) * W)}
          height={bot - top}
        />
      ))}

      {scale.mixed
        ? /* Ohne gemeinsame Achse gibt es keine Zahl, die eine Gitterlinie
             tragen könnte — drei Viertelmarken sind dann alles, was ehrlich
             ist. */
          [0.25, 0.5, 0.75].map((f) => (
            <line
              key={`${id}-g-${f}`}
              className="trace__grid"
              x1={0}
              x2={W}
              y1={top + (bot - top) * f}
              y2={top + (bot - top) * f}
            />
          ))
        : scale.ticks.map((v) => (
            <line
              key={`${id}-t-${v}`}
              className={Math.abs(v) < 1e-9 ? 'trace__zero' : 'trace__grid'}
              x1={0}
              x2={W}
              y1={y(v)}
              y2={y(v)}
            />
          ))}

      {[0.25, 0.5, 0.75].map((f) => (
        <line
          key={`${id}-v-${f}`}
          className="trace__grid"
          x1={f * W}
          x2={f * W}
          y1={top}
          y2={bot}
        />
      ))}

      {/* Das Ende der Standphase: der Zeitpunkt, an dem der Fuss abhebt. Als
          gestrichelte Marke und nicht als Feld — ein Feld über die halbe Breite
          ist bei 84 Pixeln Höhe mehr Fläche als Kurve. */}
      {stance !== undefined && stance > 0 && stance < 1 && (
        <line className="trace__stance" x1={stance * W} x2={stance * W} y1={top} y2={bot} />
      )}

      {limitLines.map((v, i) => (
        <line key={`${id}-l-${i}`} className="trace__limit" x1={0} x2={W} y1={y(v)} y2={y(v)} />
      ))}

      {series.map((s) => (
        <path
          key={`${id}-${s.key}`}
          className="trace__line"
          style={{ stroke: `var(--chart-${s.tone})` }}
          d={path(s)}
        />
      ))}

      {cursor && (
        <g>
          <line ref={headLine} className="trace__playhead" x1={0} x2={0} y1={0} y2={H} />
          {series.map((s, i) => (
            <circle
              key={`${id}-dot-${s.key}`}
              ref={(node) => {
                dots.current[i] = node
              }}
              className="trace__dot"
              r={3.4}
              cx={0}
              cy={-99}
              style={{ fill: `var(--chart-${s.tone})` }}
            />
          ))}
        </g>
      )}
    </svg>
  )
}
